const DURATION = 900;

/**
 * Draws the `statistics` section's two charts when they scroll into view.
 *
 * The bars are a CSS width transition; the ring is driven from here because a
 * conic-gradient stop is a custom property, and a custom property only animates
 * if it has been registered with `@property` — which is not something this
 * toolchain emits. Stepping it by hand works everywhere.
 *
 * Both charts are drawn in the markup by default. This only takes them down to
 * zero once it is running (the `--ready` class), so a bundle that never
 * executes leaves the section readable rather than blank.
 */
export default {
  init() {
    const charts = document.querySelectorAll('.js-best-charts');

    if (!charts.length) {
      return;
    }

    const reduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const drawRing = el => {
      const ring = el.querySelector('.js-best-ring');

      if (!ring) {
        return;
      }

      const target = parseFloat(ring.getAttribute('data-share')) || 0;

      if (reduced || !window.requestAnimationFrame) {
        ring.style.setProperty('--ring-share', `${target}%`);
        return;
      }

      const started = Date.now();
      const step = () => {
        const t = Math.min(1, (Date.now() - started) / DURATION);
        // easeOutCubic, so the arc settles rather than stopping dead.
        const eased = 1 - (1 - t) ** 3;

        ring.style.setProperty(
          '--ring-share',
          `${(target * eased).toFixed(2)}%`
        );

        if (t < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    };

    const draw = el => {
      el.classList.add('is-drawn');
      drawRing(el);
    };

    charts.forEach(el => {
      el.classList.add('best-stats__charts--ready');

      const ring = el.querySelector('.js-best-ring');

      if (ring) {
        ring.style.setProperty('--ring-share', '0%');
      }
    });

    if (reduced || !window.IntersectionObserver) {
      charts.forEach(draw);
      return;
    }

    const observer = new window.IntersectionObserver(
      (entries, obs) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            return;
          }

          // Once drawn it stays drawn; scrolling back up must not replay it.
          obs.unobserve(entry.target);
          draw(entry.target);
        });
      },
      { threshold: 0.25 }
    );

    charts.forEach(el => observer.observe(el));
  }
};
